import { useEffect, useMemo, useState } from 'react'
import { GROUP_TYPE } from '../../domain/domainCodes'
import {
  Building2,
  EllipsisVertical,
  FolderSync,
  Layers,
  Mail,
  Pencil,
  Plus,
  Trash2,
  UserRound,
  Users as UsersIcon,
} from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import { serverDetail, serverText } from '../../lib/i18n'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import IconButton from '../../components/IconButton'
import Menu, { MenuItem, MenuSeparator } from '../../components/Menu'
import NotificationsModal from '../../components/NotificationsModal'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatStrip from '../../components/StatStrip'
import GroupTypeBadge from '../../components/GroupTypeBadge'
import { useToast } from '../../components/toastContext'
import GroupMembersDrawer from './GroupMembersDrawer'
import GroupBulkModal from './GroupBulkModal'
import GroupCloneModal from './GroupCloneModal'
import GroupEmailModal from './GroupEmailModal'
import GroupFormModal from './GroupFormModal'
import RejectedRowsModal from '../../components/RejectedRowsModal'
import {
  addUsersFromExcel,
  addUsersToGroup,
  bulkAssignUsers,
  cloneDepartments,
  deleteEmptySyncDepartments,
  deleteGroup,
  listGroups,
  removeUsersFromGroup,
  saveGroup,
  sendGroupEmail,
} from './groupsService'
import { strings as S } from './strings'
import './GroupsPage.css'

/* `RejectedRowsModal` is shared with the users import and reads one flat object, so the two
   column headings it has in common with the members list are named for it here rather than
   duplicated into this screen's own strings. */
const UNKNOWN_LABELS = {
  ...S.unknownUsersModal,
  indexHeader: S.usersModal.columns.index,
  nameHeader: S.usersModal.columns.userName,
}

/**
 * الإدارات — Views/Groups/Index.cshtml + js.cshtml.
 *
 * Two of the `تحديث` menu items are conditional in the legacy view
 * (`departmentsIntegrationDataSourceExist`, `isAutoDeleteEmptySystemGroups`); the
 * demo database enables neither, so only the excel bulk update renders.
 */
export default function GroupsPage() {
  const toast = useToast()
  const [query, setQuery] = useRemembered('groups:query', '')
  const [rows, setRows] = useState([])
  /** `dbContext.GroupTypes`, for the create/edit dropdown. */
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — see the `error` prop on DataTable. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [membersOf, setMembersOf] = useState(null)
  const [emailTo, setEmailTo] = useState(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [batchesFor, setBatchesFor] = useState(null)
  /** The usernames an add could not find — see `RejectedRowsModal`. */
  const [unknown, setUnknown] = useState(null)
  /** `null` is "everything"; the other two mirror the legacy group type ids. */
  /* Remembered with the search, because between them they decide WHICH rows the page
     number counts: opening a department and coming back landed on page 3 of every group,
     unsearched — the same page number over a different list. */
  const [scope, setScope] = useRemembered('groups:scope', null)
  /** The directory integration, from the page payload — see `load`. */
  const [sync, setSync] = useState({ available: false, sources: [], purge: false })
  const [pendingPurge, setPendingPurge] = useState(false)
  const [cloning, setCloning] = useState(false)

  const load = () => {
    setLoading(true)
    return listGroups()
      .then((response) => {
        setRows(response.rows)
        setTypes(response.types)
        /* Both were already in the payload and read by nothing: whether a directory is
           configured at all, and which ones — so the two sync actions below can be offered
           only when they could work. */
        setSync({
          available: response.hasDepartmentsDataSource,
          sources: response.dataSources,
          /* As in the legacy view: removing empty synced departments is offered only
             when "delete directory groups with no users" is on — off, it removes nothing. */
          purge: response.autoDeleteEmptySystemGroups,
        })
        setLoading(false)
      })
      .catch((error) => {
        setRows([])
        setLoadError(error)
        setLoading(false)
      })
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * One place that turns an action's outcome into what the user sees.
   *
   * Each of these dialogs used to close and toast success without calling anything — the
   * delete did not even toast, it just closed. A failure now keeps its dialog open with
   * the server's own reason, which names the department still holding members or the name
   * already in use.
   */
  const run = async (call, onDone, fallback) => {
    const outcome = await call
    if (!outcome.ok) {
      toast.error(outcome.message || S.fail.action)
      return outcome
    }

    /* PARTLY DONE IS STILL DONE, so `onDone` runs and the list is reloaded either way —
       the members that were added changed the counts on the page behind. What is suppressed
       is only the success dialog: it is centred above the modal that is about to report the
       rest, and "saved successfully" over a list of names that were not is the wrong
       statement anyway. The caller raises the report instead. */
    if (!outcome.partial) toast.success(serverText(outcome.message) || fallback || S.actionDone)
    onDone?.()
    load()
    return outcome
  }

  /* Derived from the rows already on the page — a department is type 1, a
     private group is anything else, which is exactly what the type column
     renders. */
  const stats = useMemo(() => {
    const departments = rows.filter((row) => row.typeId === GROUP_TYPE.DEPARTMENTS).length
    return {
      total: rows.length,
      departments,
      privateGroups: rows.length - departments,
      members: rows.reduce((sum, row) => sum + (row.usersCount ?? 0), 0),
    }
  }, [rows])

  /* The two type cards double as the list's type filter, so the summary is not
     a read-only banner taking up a strip of the page. The other two cards opt
     out below: a total has nothing to narrow to. */
  const visibleRows = useMemo(() => {
    if (scope === 'departments') return rows.filter((row) => row.typeId === GROUP_TYPE.DEPARTMENTS)
    if (scope === 'privateGroups') return rows.filter((row) => row.typeId !== GROUP_TYPE.DEPARTMENTS)
    return rows
  }, [rows, scope])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="groups__index">{index + 1}</span>,
      },
      {
        key: 'name',
        header: S.columns.name,
        render: (row) => (
          <span className="groups__name">
            <bdi>{row.name}</bdi>
          </span>
        ),
      },
      {
        key: 'typeName',
        header: S.columns.type,
        render: (row) => <GroupTypeBadge typeId={row.typeId}>{row.typeName}</GroupTypeBadge>,
      },
      {
        key: 'createdOn',
        header: S.columns.createdOn,
        render: (row) => (
          <span className="groups__date" dir="ltr">
            {row.createdOnText}
          </span>
        ),
      },
      {
        key: 'usersCount',
        header: S.columns.usersCount,
        sortable: false,
        /* `null` is "not known" — the counts come from a second call, and a stand-in
           zero would read as an empty department. */
        render: (row) => (
          <span className="groups__count">
            {row.usersCount == null ? '—' : row.usersCount.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              {
                id: 'users',
                icon: UsersIcon,
                label: S.row.users,
                tone: 'violet',
                onClick: () => setMembersOf(row),
              },
              {
                id: 'edit',
                icon: Pencil,
                label: S.row.edit,
                onClick: () => {
                  setEditing(row)
                  setFormOpen(true)
                },
              },
              {
                id: 'email',
                icon: Mail,
                label: S.row.sendEmail,
                tone: 'success',
                onClick: () => setEmailTo(row),
              },
              {
                /* Trash2, as every other delete in the panel. A bare X reads as "close" —
                   the same glyph dismisses this very menu — and a row menu where the
                   destructive entry is drawn like a dismiss is one bad click away. */
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              <Plus size={16} strokeWidth={2} />
              {S.actions.createGroup}
            </Button>

            <Menu
              width="340px"
              trigger={({ toggle, open, id }) => (
                <IconButton
                  icon={EllipsisVertical}
                  label={S.actions.update}
                  size="md"
                  filled
                  onClick={toggle}
                  aria-haspopup="true"
                  aria-expanded={open}
                  aria-controls={id}
                />
              )}
            >
              <MenuItem icon={UsersIcon} onClick={() => setBulkOpen(true)}>
                {S.actions.bulkFromExcel}
              </MenuItem>

              {/* BOTH DIRECTORY ACTIONS were written in the service and offered nowhere.
                  They appear only when a departments data source is configured, which the
                  payload has always said and nothing has ever read — on an installation
                  with no directory they would fail every time. */}
              {sync.available && (
                <>
                  <MenuSeparator />
                  <MenuItem
                    icon={FolderSync}
                    onClick={() => setCloning(true)}
                    disabled={sync.sources.length === 0}
                  >
                    {S.actions.cloneFromDirectory}
                  </MenuItem>
                  {/* Destructive and unbounded — it removes every emptied synced
                      department at once — so it asks first. */}
                  {sync.purge && (
                    <MenuItem icon={Trash2} danger onClick={() => setPendingPurge(true)}>
                      {S.actions.purgeEmptySynced}
                    </MenuItem>
                  )}
                </>
              )}
            </Menu>
          </>
        }
      />

      <StatStrip
        label={S.stats.caption}
        activeId={scope ?? 'all'}
        onSelect={(id) => {
          /* ALL IS NOT A FILTER TO TOGGLE, it is the absence of one, so it clears the scope
             outright rather than flipping it back on when clicked twice. */
          if (id === 'all') return setScope(null)
          setScope((current) => (current === id ? null : id))
        }}
        items={[
          {
            /* Was an unclickable figure labelled "Total": the only way back to the full
               list was to click the selected card a second time. `members` below stays
               unclickable - it counts USERS, and the list has no such subset to narrow to. */
            id: 'all',
            icon: Layers,
            label: S.stats.all,
            value: stats.total,
            tone: 'violet',
          },
          {
            id: 'departments',
            icon: Building2,
            label: S.stats.departments,
            value: stats.departments,
            tone: 'info',
          },
          {
            id: 'privateGroups',
            icon: UsersIcon,
            label: S.stats.privateGroups,
            value: stats.privateGroups,
            tone: 'gold',
          },
          {
            id: 'members',
            icon: UserRound,
            label: S.stats.members,
            value: stats.members,
            tone: 'neutral',
            selectable: false,
          },
        ]}
      />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.name }} />

      <DataTable
        error={loadError}
        columns={columns}
        rows={visibleRows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'name', dir: 'asc' }}
        /* The scope card filters the rows OUTSIDE the table, so the table cannot see it:
           picking الإدارات while on page 3 kept page 3 of a list that is now one page. */
        resetKey={scope}
      />

      <GroupFormModal
        open={formOpen}
        group={editing}
        types={types}
        onClose={() => setFormOpen(false)}
        onSubmit={(values, isUpdate) =>
          run(
            saveGroup({ ...values, id: editing?.id }, isUpdate),
            () => setFormOpen(false),
            isUpdate ? S.form.updatedMessage : S.form.createdMessage,
          )
        }
      />

      {/* Members and the add-members picker are two tabs of one drawer, so adding
          people no longer closes the list you are adding them to.

          Both handlers return the outcome: the drawer only clears its selection and
          returns to the members tab once the server has actually taken the change. */}
      <GroupMembersDrawer
        open={!!membersOf}
        group={membersOf}
        /* GUARDED WHILE THE REPORT IS UP. `Drawer` listens for Escape on `document`
           unconditionally, so one press reaches both overlays and would take the drawer
           down from under the modal. */
        onClose={() => {
          if (!unknown) setMembersOf(null)
        }}
        onAddUsers={async ({ users, excelFile }) => {
          /* Read BEFORE the await: the drawer can be closed while the request is in
             flight, and `membersOf` is then null when the report needs its name. */
          const group = membersOf
          const outcome = await run(
            excelFile
              ? addUsersFromExcel({ file: excelFile, groupId: group.id })
              : addUsersToGroup({ users, groupId: group.id }),
          )

          if (outcome.partial) {
            setUnknown({
              names: outcome.unknownUsers,
              subject: group.name,
              message: serverText(outcome.message),
            })
          }

          /* Returned so the drawer clears the selection and reloads the members — a
             partial add IS an add, and the users it took are now in that list. */
          return outcome
        }}
        onRemoveUsers={(users) => run(removeUsersFromGroup({ users, groupId: membersOf.id }))}
      />

      {/* AFTER the drawer, so its portal is appended last and sits above it. `report` is a
          snapshot, so the list survives the drawer behind it being closed. */}
      <RejectedRowsModal report={unknown} labels={UNKNOWN_LABELS} onClose={() => setUnknown(null)} />

      <GroupEmailModal
        open={!!emailTo}
        group={emailTo}
        onClose={() => setEmailTo(null)}
        onShowBatches={() => setBatchesFor(emailTo)}
        onSubmit={(values) =>
          run(
            sendGroupEmail({
              groupId: emailTo.id,
              title: values?.title,
              body: values?.body,
              sendTime: values?.sendTime,
            }),
            () => setEmailTo(null),
            S.emailModal.success,
          )
        }
      />

      {/* The reply lists the rows it could not take, so a partial import says so
          instead of reporting a clean success. */}
      <GroupBulkModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSubmit={async ({ file }) => {
          const outcome = await bulkAssignUsers({ file })
          if (!outcome.ok) {
            toast.error(serverDetail(outcome.message) || S.fail.assign)
            return
          }
          setBulkOpen(false)
          toast.success(
            outcome.rejected.length
              ? `${outcome.message || S.bulkModal.success} (${outcome.rejected.length})`
              : outcome.message || S.bulkModal.success,
          )
          load()
        }}
      />

      {/* Removing every emptied synced department at once — irreversible and unbounded,
          so it names what it does rather than asking "are you sure". */}
      <ConfirmDialog
        open={pendingPurge}
        message={S.purge.confirm}
        confirmLabel={S.actions.purgeEmptySynced}
        cancelLabel={S.close}
        onConfirm={() => run(deleteEmptySyncDepartments(), () => setPendingPurge(false))}
        onCancel={() => setPendingPurge(false)}
      />

      {/* Which directory to pull from. One source is the normal case, but the payload
          returns a list, so it is a choice rather than an assumption. */}
      <GroupCloneModal
        open={cloning}
        sources={sync.sources}
        prunes={sync.purge}
        onClose={() => setCloning(false)}
        onSubmit={(sourceId) => run(cloneDepartments(sourceId), () => setCloning(false))}
      />

      <NotificationsModal
        open={!!batchesFor}
        objectId={batchesFor ? String(batchesFor.id) : null}
        templateTypes={[7]}
        onClose={() => setBatchesFor(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.row.delete}
        cancelLabel={S.close}
        /* This used to just close the dialog — the department was never deleted. */
        onConfirm={() => run(deleteGroup(pendingDelete.id), () => setPendingDelete(null))}
        onCancel={() => setPendingDelete(null)}
      />

    </>
  )
}
